import type { ArrowHead, Point2D } from './contour-draw-geometry';

import * as d3 from 'd3';

import {
  ARROW_HEAD_LENGTH,
  ARROW_HEAD_WIDTH,
  CENTROID_RADIUS,
  CONTOUR_STROKE,
  LABEL_FONT,
} from './contour-draw-constants';
import { arrowHeadGeometry, getLineGenerator } from './contour-draw-geometry';

// d3 selections are loosely typed here; these helpers are shared by the
// contour SVG overlays and all draw in image-pixel space inside a group that
// carries the image->screen matrix (see applyGroupTransform).
type Selection = d3.Selection<d3.BaseType, unknown, d3.BaseType, unknown>;

export function appendPath(
  group: Selection,
  points: Point2D[],
  color: string,
  opacity: number,
  strokeWidth: number,
) {
  group
    .append('path')
    .datum(points)
    .attr('fill', 'none')
    .attr('opacity', opacity)
    .attr('stroke', color)
    .attr('stroke-width', strokeWidth)
    .attr('vector-effect', 'non-scaling-stroke')
    .attr('d', getLineGenerator());
}

// Shared arrow shape (shaft + filled triangle head) used by both the committed
// renderer (UIDDisplayContours) and the live preview (UIDAdjustContours). All
// coordinates are in the group's image-pixel space. Returns the arrowhead
// geometry (or null for a degenerate arrow) so callers can add extra decoration
// (e.g. a selection highlight along the shaft).
export function appendArrow(
  group: Selection,
  start: Point2D,
  end: Point2D,
  color: string,
  opacity = 1,
): ArrowHead | null {
  const head = arrowHeadGeometry(start, end, ARROW_HEAD_LENGTH, ARROW_HEAD_WIDTH);
  const shaftEnd = head ? head.shaftEnd : end;

  // shaft stops at shaftEnd so the head isn't doubled
  appendPath(group, [start, shaftEnd], color, opacity, CONTOUR_STROKE);

  if (head) {
    group
      .append('path')
      .datum([end, head.leftBase, head.rightBase, end])
      .attr('fill', color)
      .attr('stroke', 'none')
      .attr('opacity', opacity)
      .attr('d', getLineGenerator());
  }

  return head;
}

export interface CircleOpts {
  fill?: string;
  fillOpacity?: number;
  opacity?: number;
  stroke?: string;
  strokeWidth?: number;
}

export function appendCircle(
  group: Selection,
  cx: number,
  cy: number,
  radius: number,
  opts: CircleOpts = {},
) {
  const circle = group
    .append('circle')
    .attr('cx', cx)
    .attr('cy', cy)
    .attr('original-r', radius)
    .attr('r', radius)
    .attr('fill', opts.fill ?? 'none');
  if (opts.fillOpacity != null) circle.attr('fill-opacity', opts.fillOpacity);
  if (opts.opacity != null) circle.attr('opacity', opts.opacity);
  if (opts.stroke) {
    circle
      .attr('stroke', opts.stroke)
      .attr('stroke-width', opts.strokeWidth ?? 1)
      .attr('vector-effect', 'non-scaling-stroke');
  }
  return circle;
}

export function appendLabel(
  group: Selection,
  x: number,
  y: number,
  text: string | number,
  font: number = LABEL_FONT,
) {
  group
    .append('text')
    .attr('x', x)
    .attr('y', y)
    .attr('fill', 'white')
    .attr('original-font-size', font)
    .attr('font-size', `${font}px`)
    .attr('text-anchor', 'middle')
    .attr('dy', '-1')
    .text(text);
}

export function appendSelectionHandle(
  group: Selection,
  centroidPosition: { x: number; y: number },
  color: string,
  moved: boolean,
) {
  appendCircle(group, centroidPosition.x, centroidPosition.y, CENTROID_RADIUS, {
    fill: color,
    fillOpacity: moved ? 0.5 : 0.3,
  });
}

export interface GroupTransformCtx {
  overlayTransform: string;
  scaleFactor: number;
  imageToScreenMatrix: { a: number; b: number };
}

// Applies the image->screen affine to the contour group and keeps radii/fonts
// at constant on-screen size (`non-scaling-stroke` handles strokes). Text is
// also counter-rotated so annotations stay upright on rotated images.
export function applyGroupTransform(group: Selection, ctx: GroupTransformCtx) {
  if (!group || group.empty?.()) return;

  group.attr('transform', ctx.overlayTransform);

  // Only counter-scale handles that opted in via `original-r`; raw circles
  // (e.g. the nudge tool) are meant to scale with the image and are left alone.
  const sf = ctx.scaleFactor || 1;
  group
    .selectAll('circle[original-r]')
    .attr('r', function applyRadius(this: d3.BaseType) {
      const node = this as Element;
      const r = Number.parseFloat(node.getAttribute('original-r') ?? '');
      return Number.isFinite(r) ? r / sf : node.getAttribute('r');
    });

  const { a, b } = ctx.imageToScreenMatrix;
  const angleDeg = (Math.atan2(b, a) * 180) / Math.PI;
  group.selectAll('text').each(function applyText(this: d3.BaseType) {
    const node = this as Element;
    const el = d3.select(node);
    const f
      = Number.parseFloat(node.getAttribute('original-font-size') ?? '')
        || LABEL_FONT;
    el.attr('font-size', `${f / sf}px`);
    const x = Number.parseFloat(node.getAttribute('x') ?? '') || 0;
    const y = Number.parseFloat(node.getAttribute('y') ?? '') || 0;
    el.attr('transform', `rotate(${-angleDeg}, ${x}, ${y})`);
  });
}
